/**
 * Вкладка «Замечания» карточки шаблона AI «Чатов» (`/templates?tab=ai&t=N&pane=remarks`): владелец говорит
 * или пишет, что в шаблоне не так, — к шаблону целиком или к выделенному куску текста. Замечание ложится в
 * общую таблицу замечаний AI Lab (комната `tpl-<id>/v<N>`, канал «чат»); разбирает модель командой
 * `voice-lab-remarks`, ответ — под замечанием, правка — новой версией шаблона. Право — `voice_lab`.
 * Спека `docs/specs/2026-11-04-ai-template-remarks-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { MessageSquarePlus } from "lucide-react";
import { useRef, useState } from "react";

import type { AiTemplate } from "@/lib/aiTemplates";
import { templateRemarksQuery } from "@/lib/voiceLab";
import { RemarkChip, RemarkForm } from "./VoiceLabRemarks";

const ЦИТАТА = 600; // столько знаков выделенного уходит в замечание (`remarks.ЦИТАТА`)
const ПОЛЯ = [
  ["text", "Текст"],
  ["alt", "Запасной текст"],
  ["instruction", "Инструкция модели"],
] as const;

function выделение(box: HTMLElement | null): string {
  const sel = window.getSelection();
  if (!box || !sel || sel.isCollapsed || !sel.anchorNode || !box.contains(sel.anchorNode)) return "";
  return sel.toString().trim();
}

export function AiTemplateRemarks({ template }: { template: AiTemplate }) {
  const q = useQuery(templateRemarksQuery(template.id));
  const box = useRef<HTMLDivElement>(null);
  const [выделено, setВыделено] = useState("");
  const [форма, setФорма] = useState<string | null>(null); // null — закрыта, "" — к шаблону целиком
  const список = q.data?.remarks ?? [];
  const ждут = список.filter((r) => ["new", "waiting_voice", "voice_failed"].includes(r.status)).length;
  return (
    <section className="flex flex-col gap-3 pt-3" data-annot="замечания к шаблону">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <button
          type="button"
          onClick={() => setФорма("")}
          className="inline-flex items-center gap-1.5 rounded-full border border-accent px-3 py-1 font-medium text-accent hover:bg-accent/10"
        >
          <MessageSquarePlus size={14} aria-hidden />
          Замечание к шаблону
        </button>
        <span className="text-fg-muted">
          Голосом или текстом: разбирает модель, ответ появится под замечанием, правка — новой версией шаблона
          {ждут ? ` · ждут разбора: ${ждут}` : ""}
        </span>
      </div>
      {форма != null && (
        <div className="flex flex-col gap-1">
          {форма && (
            <div className="max-w-xl whitespace-pre-wrap border-l-2 border-accent pl-2 text-[12px] text-fg-muted">
              «{форма.length > 300 ? `${форма.slice(0, 300)}…` : форма}»
            </div>
          )}
          <RemarkForm
            room={`tpl-${template.id}/v${template.version}`}
            template={{ id: template.id, version: template.version, quote: форма.slice(0, ЦИТАТА) }}
            onClose={() => setФорма(null)}
            onSaved={() => setВыделено("")}
          />
        </div>
      )}
      <div
        ref={box}
        className="flex max-w-3xl flex-col gap-2 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3 text-[13px]"
        onMouseUp={() => setВыделено(выделение(box.current))}
        onKeyUp={() => setВыделено(выделение(box.current))}
        data-annot="текст шаблона"
      >
        <div className="text-[11.5px] text-fg-muted">
          Действующая версия v{template.version} · выделите кусок — появится «Замечание к выделенному»
        </div>
        {выделено && форма == null && (
          <div className="flex min-w-0 items-center gap-2 rounded-[var(--radius-ui)] border border-accent bg-bg px-2 py-1 text-[12px]">
            <span className="min-w-0 flex-1 truncate text-fg-muted" title={выделено.slice(0, ЦИТАТА)}>
              Выделено: «{выделено.replace(/\s+/g, " ").slice(0, 200)}»
            </span>
            <button
              type="button"
              className="shrink-0 rounded-full bg-accent px-3 py-0.5 font-medium text-white"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setФорма(выделено)}
            >
              Замечание к выделенному
            </button>
          </div>
        )}
        <div className="font-medium">{template.question}</div>
        {ПОЛЯ.filter(([к]) => template[к]).map(([к, имя]) => (
          <div key={к} className="flex flex-col gap-0.5">
            <span className="text-[11.5px] text-fg-muted">{имя}</span>
            <div className="whitespace-pre-wrap">{template[к]}</div>
          </div>
        ))}
      </div>
      {q.isError && <div className="text-[12.5px] text-err">Замечания не загрузились</div>}
      {q.data && !список.length && <div className="text-[12.5px] text-fg-muted">Замечаний к шаблону пока нет</div>}
      {список.length > 0 && (
        <div className="flex flex-col gap-1.5" data-annot="список замечаний к шаблону">
          {список.map((r) => (
            <div key={r.id} className="flex flex-col gap-0.5">
              <span className="text-[11.5px] text-fg-muted">к версии {r.room.split("/")[1]}</span>
              <RemarkChip r={r} showQuote />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
